import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";

const STORAGE_KEY = "ak_cookie_consent_v1";

type Consent = "accepted" | "rejected" | null;

function readConsent(): Consent {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "accepted" || v === "rejected" ? v : null;
}

function applyConsent(value: "accepted" | "rejected") {
  window.localStorage.setItem(STORAGE_KEY, value);
  // Google Consent Mode v2 — signalera till AdSense/Analytics
  const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
  w.dataLayer = w.dataLayer || [];
  const gtag = w.gtag || function (...args: unknown[]) { w.dataLayer!.push(args); };
  w.gtag = gtag;
  gtag("consent", "update", {
    ad_storage: value === "accepted" ? "granted" : "denied",
    ad_user_data: value === "accepted" ? "granted" : "denied",
    ad_personalization: value === "accepted" ? "granted" : "denied",
    analytics_storage: value === "accepted" ? "granted" : "denied",
  });
}

export function CookieConsent() {
  const [state, setState] = useState<Consent>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Default: deny allt tills användaren väljer (Consent Mode v2)
    const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
    w.dataLayer = w.dataLayer || [];
    const gtag = w.gtag || function (...args: unknown[]) { w.dataLayer!.push(args); };
    w.gtag = gtag;
    gtag("consent", "default", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
      wait_for_update: 500,
    });

    const existing = readConsent();
    if (existing) applyConsent(existing);
    setState(existing);
    setMounted(true);
  }, []);

  if (!mounted || state !== null) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] p-4 md:p-6">
      <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-card/95 p-5 shadow-2xl backdrop-blur-md md:p-6">
        <p className="text-xs font-medium uppercase tracking-wider text-accent">Cookies</p>
        <p className="mt-2 text-sm leading-relaxed text-foreground">
          Vi använder cookies för att sidan ska fungera, för statistik och för annonser via Google AdSense.
          Du kan välja själv — ditt val sparas i 12 månader.{" "}
          <Link to="/cookies" className="underline decoration-accent underline-offset-2 hover:text-accent">
            Läs mer
          </Link>
          .
        </p>
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            onClick={() => {
              applyConsent("rejected");
              setState("rejected");
            }}
            className="rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium transition hover:bg-secondary"
          >
            Endast nödvändiga
          </button>
          <button
            onClick={() => {
              applyConsent("accepted");
              setState("accepted");
            }}
            className="rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
          >
            Godkänn alla
          </button>
        </div>
      </div>
    </div>
  );
}
